/**
 * storage-config.ts
 *
 * Configuration for storage priorities to optimize initial load
 *
 * Primary responsibilities:
 * - Define which atoms should use critical storage
 * - Define which atoms can use lazy storage
 * - Provide helper functions for atom categorization
 */

import { criticalLazyStorage, lazyStorage } from './lazy-storage'

// Define critical storage keys that should be loaded immediately
// These are essential for app functionality
export const CRITICAL_STORAGE_KEYS = [
  'themeMode', // User theme preference
  'showSidebar', // UI layout preference
  'userPreferences' // User preferences
]

// Define non-critical but still important keys that can be loaded with a delay
export const IMPORTANT_STORAGE_KEYS = [
  'chatSettings', // Chat configurations
  'keyboardShortcutsEnabled',
  'sidebarWidth'
]

/**
 * Get the appropriate storage implementation based on key
 * Critical keys will use storage that loads on init, others use lazy storage
 */
export const getStorageForKey = (key: string) => {
  if (CRITICAL_STORAGE_KEYS.includes(key)) {
    return criticalLazyStorage
  }
  return lazyStorage
}

/**
 * Utilities for storage keys
 */

// Clear all storage for development/testing
export const clearAllStorage = () => {
  if (typeof localStorage !== 'undefined') {
    localStorage.clear()
  }
}

// Helper to get all persisted keys
export const getStoredKeys = () => {
  if (typeof localStorage === 'undefined') {
    return []
  }

  const keys = []
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i)
    if (key) {
      keys.push(key)
    }
  }
  return keys
}
